/**
 * <AskAssistantSection> — §8 of <ZoomPanel>: patient-pinned BeAssistant chat
 * for the treating physician during the call. The conversation starts lazily
 * on the first question (`assistantStart({ context: { patientId } })`) so an
 * untouched section costs nothing; the threadPath lives in CallSessionProvider
 * so the thread survives rail remounts (rail↔fullscreen, route changes).
 *
 * Rail rendering is deliberately lean: the answer's markdown + simple tables
 * only. Charts/KPIs/follow-up suggestions are suppressed by the pinned-patient
 * prompt block server-side and ignored here; `artifacts.request` picker
 * actions never render. Internal links confirm before navigating — SPA
 * navigation unmounts the rail while the call keeps running.
 */
import { useCallback, useEffect, useRef, type ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import { useNavigate } from 'react-router-dom'
import { ActionIcon, Group, Loader } from '@mantine/core'
import { modals } from '@mantine/modals'
import { IconChevronDown, IconChevronUp, IconSparkles } from '@tabler/icons-react'
import type { AssistantAnswer, ChatEntry } from '@/pages/Assistant/useAssistant'
import { InputBar } from '@/pages/Assistant/components/InputBar'
import { cn } from '@/utils/cn'
import { scribeText } from './scribeText'
import { useAssistantThread } from './useAssistantThread'

type AnswerTable = NonNullable<NonNullable<AssistantAnswer['artifacts']>['tables']>[number]

export type AskAssistantSectionProps = {
  patientId: string
  collapsed: boolean
  onToggleCollapsed: () => void
  threadPath: string[] | null
  onThreadPathChange: (path: string[] | null) => void
}

export function AskAssistantSection({
  patientId,
  collapsed,
  onToggleCollapsed,
  threadPath,
  onThreadPathChange,
}: AskAssistantSectionProps) {
  const { entries, streaming, error, send, cancel } = useAssistantThread({
    patientId,
    threadPath,
    onThreadPathChange,
  })
  const navigate = useNavigate()

  const confirmNavigate = useCallback(
    (href: string) => {
      modals.openConfirmModal({
        title: 'Leave the call view?',
        children: (
          <p className="text-sm text-slate-600">
            The call keeps running in the background, but the video panel will close on this page.
          </p>
        ),
        labels: { confirm: 'Open page', cancel: 'Stay' },
        onConfirm: () => void navigate(href),
      })
    },
    [navigate],
  )

  // Pin the scroller to the newest message while a conversation is active.
  const scrollerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = scrollerRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [entries, streaming])

  const bubbles = toBubbles(entries)

  return (
    <div
      data-testid="zoom-panel-ask-assistant"
      className={cn(
        'flex flex-col overflow-hidden border-t border-white/[0.07]',
        collapsed ? 'shrink-0' : 'min-h-0 flex-1',
      )}
    >
      <Group
        justify="space-between"
        align="center"
        wrap="nowrap"
        className="shrink-0 cursor-pointer px-[14px] py-[9px] select-none"
        onClick={onToggleCollapsed}
      >
        <Group gap={6} align="center" wrap="nowrap" className="min-w-0">
          <IconSparkles size={11} style={{ color: '#7dd3fc' }} aria-hidden="true" />
          <span className={`${scribeText.eyebrow} text-slate-500`}>Ask BeAssistant</span>
          {streaming && <Loader size={10} color="gray" />}
        </Group>
        <ActionIcon
          variant="subtle"
          size="xs"
          color="gray"
          aria-label={collapsed ? 'Expand ask BeAssistant' : 'Collapse ask BeAssistant'}
          onClick={(e) => {
            e.stopPropagation()
            onToggleCollapsed()
          }}
        >
          {collapsed ? <IconChevronDown size={14} /> : <IconChevronUp size={14} />}
        </ActionIcon>
      </Group>

      {!collapsed && (
        <>
          <div
            ref={scrollerRef}
            className={
              'min-h-0 flex-1 overflow-y-auto px-[14px] pb-[10px] ' +
              '[scrollbar-color:rgba(148,163,184,0.35)_transparent] [scrollbar-width:thin] ' +
              '[&::-webkit-scrollbar]:w-[6px] ' +
              '[&::-webkit-scrollbar-track]:bg-transparent ' +
              '[&::-webkit-scrollbar-thumb]:rounded-full ' +
              '[&::-webkit-scrollbar-thumb]:bg-slate-500/40 ' +
              '[&::-webkit-scrollbar-thumb]:transition-colors ' +
              'hover:[&::-webkit-scrollbar-thumb]:bg-slate-400/60'
            }
          >
            {bubbles.length === 0 && !streaming && (
              <div className={`${scribeText.body} text-slate-500 italic`}>
                Ask about this patient&apos;s chart — labs, meds, history, appointments…
              </div>
            )}
            <div className="flex flex-col gap-2">
              {bubbles.map((bubble) =>
                bubble.role === 'user' ? (
                  <div
                    key={bubble.id}
                    className={`${scribeText.body} max-w-[85%] self-end rounded-lg border border-white/[0.08] bg-white/[0.08] px-2.5 py-1.5 leading-relaxed whitespace-pre-wrap text-slate-200`}
                  >
                    {bubble.text}
                  </div>
                ) : (
                  <div
                    key={bubble.id}
                    className="max-w-full self-start rounded-lg border border-[#1e293b] bg-[#0b1220] px-2.5 py-1.5"
                  >
                    <AssistantBubbleBody answer={bubble.answer} onNavigate={confirmNavigate} />
                  </div>
                ),
              )}
              {streaming && !lastTurnHasAnswer(entries) && (
                <Group gap={6} align="center" className="self-start px-1 py-0.5">
                  <Loader size="xs" color="gray" />
                  <span className={`${scribeText.meta} text-slate-500`}>Thinking…</span>
                </Group>
              )}
            </div>
            {error && (
              <div className={`${scribeText.meta} mt-2 text-red-400`} role="alert">
                {error}
              </div>
            )}
          </div>
          <div className="shrink-0">
            <InputBar
              variant="dark"
              streaming={streaming}
              onSubmit={send}
              onCancel={() => void cancel()}
            />
          </div>
        </>
      )}
    </div>
  )
}

type Bubble =
  | { role: 'user'; id: string; text: string }
  | { role: 'assistant'; id: string; answer: AssistantAnswer }

// Flatten wire turns into rail bubbles. The seeded page greeting (an
// assistant-only turn before any user turn) is skipped — the rail has its own
// patient-scoped hint and the greeting's wording is page-flavored.
function toBubbles(entries: ChatEntry[]): Bubble[] {
  const bubbles: Bubble[] = []
  let sawUser = false
  for (const entry of entries) {
    if (entry.kind !== 'turn') continue
    if (entry.user) {
      sawUser = true
      bubbles.push({ role: 'user', id: `${entry.id}-u`, text: entry.user.text })
    }
    if (entry.assistant && sawUser) {
      bubbles.push({ role: 'assistant', id: `${entry.id}-a`, answer: entry.assistant })
    }
  }
  return bubbles
}

function lastTurnHasAnswer(entries: ChatEntry[]): boolean {
  const last = entries[entries.length - 1]
  return last?.kind === 'turn' ? last.assistant !== undefined : false
}

function AssistantBubbleBody({
  answer,
  onNavigate,
}: {
  answer: AssistantAnswer
  onNavigate: (href: string) => void
}) {
  if (answer.status === 'cancelled') {
    return <div className={`${scribeText.body} text-slate-500 italic`}>Response cancelled.</div>
  }
  if (answer.status === 'error') {
    return (
      <div className={`${scribeText.body} text-red-400`}>
        Something went wrong with this response. Please ask again.
      </div>
    )
  }
  const text = answer.artifacts?.answer ?? ''
  const tables = answer.artifacts?.tables ?? []
  return (
    <>
      {text && <RailMarkdown onNavigate={onNavigate}>{text}</RailMarkdown>}
      {tables.map((table, index) => (
        <RailTable key={`${table.title}-${index}`} table={table} onNavigate={onNavigate} />
      ))}
    </>
  )
}

// Minimal dark markdown on the rail's type scale — deliberately not the
// typography plugin (its size presets fight the 12px scale).
function RailMarkdown({
  children,
  inline = false,
  onNavigate,
}: {
  children: string
  inline?: boolean
  onNavigate: (href: string) => void
}) {
  const linkRenderer = ({
    href,
    children: linkChildren,
  }: {
    href?: string
    children?: ReactNode
  }) => (
    <a
      href={href}
      className="text-[#93c5fd] hover:underline"
      onClick={(e) => {
        e.preventDefault()
        if (!href) return
        if (/^https?:\/\//i.test(href)) {
          window.open(href, '_blank', 'noopener,noreferrer')
          return
        }
        onNavigate(href)
      }}
    >
      {linkChildren}
    </a>
  )
  return (
    <div
      className={cn(
        `${scribeText.body} leading-relaxed text-slate-300`,
        '[&_p]:my-0 [&_p+p]:mt-1',
        '[&_li]:my-0 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-4 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-4',
        '[&_code]:rounded [&_code]:bg-white/[0.08] [&_code]:px-1',
        '[&_strong]:text-slate-100',
        inline && 'inline [&_p]:inline',
      )}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={{ a: linkRenderer }}>
        {children}
      </ReactMarkdown>
    </div>
  )
}

function RailTable({
  table,
  onNavigate,
}: {
  table: AnswerTable
  onNavigate: (href: string) => void
}) {
  return (
    <div className="mt-1.5 overflow-x-auto rounded-md border border-[#1e293b]">
      <table className="w-full border-collapse text-[10.5px]">
        <thead>
          <tr>
            {table.columns.map((column) => (
              <th
                key={column}
                className="border-b border-[#1e293b] bg-white/[0.04] px-2 py-1 text-left font-semibold text-slate-400"
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className="border-b border-[#1e293b]/60 px-2 py-1 align-top text-slate-300"
                >
                  {cell === null || cell === '' ? (
                    '—'
                  ) : (
                    // Cells carry markdown (patient/document links) per the
                    // assistant's table contract — render, don't show raw syntax.
                    <RailMarkdown inline onNavigate={onNavigate}>
                      {String(cell)}
                    </RailMarkdown>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
